import { useClientTheme } from "@/app/components/ThemeContext"

export interface DataCardField {
  label: string
  value: React.ReactNode
  // Render the value in the data/mono font (for numeric columns).
  mono?: boolean
  // Span both columns of the field grid (for long values like well lists).
  full?: boolean
}

interface DataCardProps {
  title: React.ReactNode
  // Optional top-right element — a badge, link, or actions cluster.
  accessory?: React.ReactNode
  fields: DataCardField[]
  // Optional bottom row, divided from the fields — used for action buttons
  // (edit / delete) on the write-page card reflows.
  footer?: React.ReactNode
  onClick?: () => void
}

// Mobile reflow primitive: one table row rendered as a stacked card.
// Shared by the read tables (Inventory, Transactions, ORRI) so they present
// consistently below the mobile breakpoint.
export function DataCard({ title, accessory, fields, footer, onClick }: DataCardProps) {
  const { theme } = useClientTheme()

  return (
    <div
      onClick={onClick}
      style={{
        border: `1px solid ${theme.borderSubtle}`,
        backgroundColor: theme.bgSurface,
        borderRadius: "12px",
        padding: "14px 16px",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div className="flex items-start justify-between gap-2" style={{ marginBottom: "12px" }}>
        <span
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "15px",
            fontWeight: 600,
            color: theme.textPrimary,
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </span>
        {accessory && <div className="flex-shrink-0">{accessory}</div>}
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-3">
        {fields.map((f, i) => (
          <div key={i} className={`flex flex-col gap-1 ${f.full ? "col-span-2" : ""}`}>
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: theme.textDim,
              }}
            >
              {f.label}
            </span>
            <span
              style={{
                fontFamily: f.mono ? "var(--font-data)" : "var(--font-ui)",
                fontSize: "13px",
                color: theme.textPrimary,
              }}
            >
              {f.value}
            </span>
          </div>
        ))}
      </div>

      {footer && (
        <div
          className="flex items-center justify-end gap-2 flex-wrap"
          style={{
            marginTop: "14px",
            paddingTop: "12px",
            borderTop: `1px solid ${theme.borderSubtle}`,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  )
}
